import classNames from "classnames";
import { useResizeObserver } from "client/Hooks";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { Icon } from "../Icon/Icon";
import { PagerBase } from "../Pager/PagerBase";
import { StepIndicator, StepStatus } from "./StepIndicator/StepIndicator";
import styles from "./Stepper.module.less";

export type Step<TValue> = {
	value: TValue;
	title: string;
	hasError?: boolean;
	isFinished?: boolean;
	valueTrackingId?: string;
};

export type StepperProps<TValue> = {
	value: TValue | nil;
	onChangeStep(value: TValue): void;
	steps: readonly Step<TValue>[];
	trackingId?: string;
};

export const Stepper = <TValue,>(props: StepperProps<TValue>): ReactElement => {
	const {
		value,
		onChangeStep,
		steps,
		trackingId
	} = props;

	const [widthByStep, setWidthByStep] = useState<readonly number[]>([]);

	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const ulRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);

	const { width: containerWidth } = useResizeObserver(containerRef);
	const { width: ulWidth } = useResizeObserver(ulRef);

	const stepStatuses = useMemo(
		(): readonly StepStatus[] => {
			return steps.map((s: Step<TValue>): StepStatus => {
				if (s.value === value) {
					return "active";
				}
				if (s.hasError) {
					return "hasError";
				}
				if (s.isFinished) {
					return "finished";
				}
				return "unfinished";
			});
		},
		[steps, value]
	);

	const newWidthHandler = useCallback(
		(newWidth: number, index: number): void => {
			setWidthByStep(widthByStep => {
				if (widthByStep[index] === newWidth) {
					return widthByStep;
				}

				const copy: number[] = [...widthByStep];
				copy[index] = newWidth;
				return copy;
			});
		},
		[]
	);

	const hasScroll = useMemo(
		(): boolean | null => {
			const { current } = ulRef;
			if (!current) {
				return null;
			}
			const totalWidthOfStepIndicators: number = widthByStep
				.reduce(
					(p, c) => p + c,
					0
				);
			return totalWidthOfStepIndicators > containerWidth;
		},
		[containerWidth, widthByStep]
	);

	const scrollToPageHandler = useCallback(
		(direction: "next" | "prev"): void => {
			const { current } = ulRef;
			if (!current) {
				return;
			}

			const scrollLeft: number = current.scrollLeft;
			const maxScrollLeft: number = current.scrollWidth - ulWidth;
			const nextPage: number = scrollLeft + ulWidth > maxScrollLeft
				? maxScrollLeft
				: scrollLeft + ulWidth;
			const prevPage: number = scrollLeft - ulWidth < 0
				? 0
				: scrollLeft - ulWidth;

			switch(direction) {
				case "next":
					current.scrollTo(nextPage, 0);
					break;
				case "prev":
					current.scrollTo(prevPage, 0);
					break;
				default:
					never(direction);
			}
		},
		[ulWidth]
	);

	const activeStepIndex = useMemo(
		(): number => steps.findIndex((item) => item.value === value),
		[steps, value]
	);

	useEffect(
		() => {
			const { current } = ulRef;

			const timeoutHandle: SetTimeoutHandle = setTimeout(
				() => {
					if (activeStepIndex > -1) {
						current?.children[activeStepIndex]
							.scrollIntoView({
								behavior: "smooth"
							});
					}
				},
				1
			);

			return () => clearTimeout(timeoutHandle);
		},
		[activeStepIndex]
	);

	const getItemValueTrackingId = useCallback(
		(step: Step<TValue>): string => {
			if (isNotNilOrEmpty(step.valueTrackingId)) {
				return step.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(step.value);
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
			ref={containerRef}
		>
			{hasScroll && (
				<div
					className={classNames(
						styles.arrowContainer,
						styles.arrowContainer_left
					)}
				>
					<PagerBase>
						<PagerBase.Button
							kind="arrow"
							onClick={() => scrollToPageHandler("prev")}
						>
							<Icon value="chevron-left" />
						</PagerBase.Button>
					</PagerBase>
				</div>
			)}
			<ul
				className={styles.stepNavigation}
				ref={ulRef}
			>
				{steps.map(
					(x, index) => (
						<StepIndicator
							key={index}
							stepNumber={index+1}
							onClick={() => onChangeStep(x.value)}
							onNewWidth={(newWidth: number) => newWidthHandler(newWidth, index)}
							status={stepStatuses[index]}
							title={x.title}
							trackingId={generateTrackingId(trackingId, `__step|${getItemValueTrackingId(x)}`)}
						/>
					)
				)}
			</ul>
			{hasScroll && (
				<div
					className={classNames(
						styles.arrowContainer,
						styles.arrowContainer_right
					)}
				>
					<PagerBase>
						<PagerBase.Button
							kind="arrow"
							onClick={() => scrollToPageHandler("next")}
						>
							<Icon value="chevron-right" />
						</PagerBase.Button>
					</PagerBase>
				</div>
			)}
		</div>
	);
};
Stepper.displayName = "Stepper";
